<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BreadcrumbTitle from 'dashboard/components-next/breadcrumb/BreadcrumbTitle.vue';
import { usePricingRulesStore } from 'dashboard/stores/pricingRules';
import {
  isIntegrationPricingPlan,
  isReadOnlyPricingPlan,
} from 'dashboard/composables/useShijiBookingLink';

const store = useStore();
const pricingRulesStore = usePricingRulesStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const alertMessage = useAlert;

const planId = computed(() => Number(route.params.planId));
const searchQuery = ref('');

const loading = computed(() => pricingRulesStore.uiFlags.isFetching);
const plan = computed(
  () => store.getters['pricingPlans/getPlanById'](planId.value) || {}
);
const isIntegrationPlan = computed(() => isIntegrationPricingPlan(plan.value));
const isExperiencePlan = computed(
  () => !!plan.value.experience_id && !isIntegrationPlan.value
);
const isReadOnlyPlan = computed(() => isReadOnlyPricingPlan(plan.value));
const readOnlyPlanTooltip = computed(() => {
  if (isIntegrationPlan.value) return t('PRICING.PLANS.MANAGED_BY_INTEGRATION');
  if (isExperiencePlan.value) return t('PRICING.PLANS.MANAGED_BY_EXPERIENCE');
  return undefined;
});
const rules = computed(() => pricingRulesStore.records);
const items = computed(() => store.getters['pricingItems/getRecords'] || []);

// Flat list of rules enriched with item info
const enrichedRules = computed(() =>
  rules.value.map(rule => {
    const item = items.value.find(i => i.id === rule.pricing_item_id);
    return {
      ...rule,
      itemName: item?.name || String(rule.pricing_item_id),
      itemIcon: item?.space_id ? 'i-lucide-orbit' : 'i-lucide-code',
    };
  })
);

const filteredRules = computed(() => {
  if (!searchQuery.value) return enrichedRules.value;
  const q = searchQuery.value.toLowerCase();
  return enrichedRules.value.filter(r => r.itemName.toLowerCase().includes(q));
});

const clauseCount = rule => {
  const clauses = rule.clauses || [];
  return clauses.filter(c => !c.is_default).length;
};

const bundles = computed(() => {
  const raw = plan.value.bundles || [];
  return raw.map(bundle => {
    const rootItem = items.value.find(i => i.id === bundle.pricing_item_id);
    const includedItems = (bundle.included_items || []).map(inc => {
      const pi = items.value.find(i => i.id === inc.pricing_item_id);
      return {
        ...inc,
        name: pi?.name || `Item #${inc.pricing_item_id}`,
        category: pi?.category,
        unit: pi?.prices?.[0]?.unit,
      };
    });
    return {
      pricing_item_id: bundle.pricing_item_id,
      rootName: rootItem?.name || `Item #${bundle.pricing_item_id}`,
      includedItems,
    };
  });
});

onMounted(async () => {
  await Promise.all([
    store.dispatch('pricingPlans/show', planId.value),
    pricingRulesStore.get(planId.value),
    items.value.length ? null : store.dispatch('pricingItems/get'),
  ]);
});

const openRule = rule => {
  openEditRule(rule);
};

const openCreateRule = () => {
  router.push({
    name: 'settings_pricing_rule_edit',
    params: { planId: planId.value, ruleId: 'new' },
  });
};

const openEditRule = rule => {
  router.push({
    name: 'settings_pricing_rule_edit',
    params: { planId: planId.value, ruleId: rule.id },
  });
};

const handleDeleteRule = async rule => {
  try {
    await pricingRulesStore.delete({
      planId: planId.value,
      id: rule.id,
    });
    alertMessage(t('PRICING.RULES.DELETE_SUCCESS'));
  } catch {
    alertMessage(t('PRICING.RULES.DELETE_ERROR'));
  }
};

const saving = ref(false);
const planForm = ref({
  name: '',
  valid_from: '',
  valid_to: '',
  repeat_yearly: false,
  is_default: false,
});

watch(
  plan,
  newPlan => {
    if (newPlan?.id) {
      planForm.value = {
        name: newPlan.name || '',
        valid_from: newPlan.valid_from || '',
        valid_to: newPlan.valid_to || '',
        repeat_yearly: newPlan.repeat_yearly || false,
        is_default: newPlan.is_default || false,
      };
    }
  },
  { immediate: true }
);

const handleSavePlan = async () => {
  saving.value = true;
  try {
    const data = { ...planForm.value };
    if (!data.valid_from) data.valid_from = null;
    if (!data.valid_to) data.valid_to = null;
    await store.dispatch('pricingPlans/update', { id: planId.value, ...data });
    alertMessage(t('PRICING.PLANS.UPDATE_SUCCESS'));
  } catch {
    alertMessage(t('PRICING.PLANS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const breadcrumbItems = computed(() => [
  { label: t('PRICING.PLANS.TITLE'), routeName: 'settings_pricing_plans' },
  { label: plan.value.name || t('PRICING.RULES.TITLE') },
]);

const handleBreadcrumbClick = item => {
  if (item.routeName) {
    router.push({ name: item.routeName });
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PRICING.LOADING')"
    :no-records-found="!loading && rules.length === 0"
    :no-records-message="t('PRICING.RULES.EMPTY')"
  >
    <template #preBody>
      <!-- Managed by experience notice -->
      <div
        v-if="isIntegrationPlan"
        class="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-n-amber-6 bg-n-amber-3 text-n-amber-11 text-sm mb-6"
      >
        <Icon icon="i-lucide-lock" class="size-4 shrink-0" />
        <span>{{ t('PRICING.PLANS.MANAGED_BY_INTEGRATION') }}</span>
      </div>
      <div
        v-if="isExperiencePlan"
        class="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-n-amber-6 bg-n-amber-3 text-n-amber-11 text-sm mb-6"
      >
        <Icon icon="i-lucide-lock" class="size-4 shrink-0" />
        <span>{{ t('PRICING.PLANS.MANAGED_BY_EXPERIENCE') }}</span>
      </div>
      <div
        v-tooltip="isReadOnlyPlan ? readOnlyPlanTooltip : undefined"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-5 mb-6 flex flex-col gap-4"
        :class="{ 'cursor-not-allowed': isReadOnlyPlan }"
      >
        <div class="grid grid-cols-2 gap-4">
          <!-- Name -->
          <div class="col-span-2">
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('PRICING.PLAN_DIALOG.NAME_LABEL') }}
            </label>
            <input
              v-model="planForm.name"
              type="text"
              :disabled="isReadOnlyPlan"
              class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <!-- Valid From -->
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('PRICING.PLAN_DIALOG.VALID_FROM_LABEL') }}
            </label>
            <input
              v-model="planForm.valid_from"
              type="date"
              :disabled="isReadOnlyPlan"
              class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <!-- Valid To -->
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('PRICING.PLAN_DIALOG.VALID_TO_LABEL') }}
            </label>
            <input
              v-model="planForm.valid_to"
              type="date"
              :disabled="isReadOnlyPlan"
              class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-6">
            <label
              class="flex items-center gap-2 text-sm text-n-slate-11"
              :class="[
                isReadOnlyPlan ? 'cursor-not-allowed' : 'cursor-pointer',
              ]"
            >
              <input
                v-model="planForm.repeat_yearly"
                type="checkbox"
                :disabled="isReadOnlyPlan"
                class="reset-base"
              />
              {{ t('PRICING.PLAN_DIALOG.REPEAT_YEARLY') }}
            </label>
            <label
              class="flex items-center gap-2 text-sm text-n-slate-11"
              :class="[
                isReadOnlyPlan ? 'cursor-not-allowed' : 'cursor-pointer',
              ]"
            >
              <input
                v-model="planForm.is_default"
                type="checkbox"
                :disabled="isReadOnlyPlan"
                class="reset-base"
              />
              {{ t('PRICING.PLAN_DIALOG.IS_DEFAULT_LABEL') }}
            </label>
          </div>
          <span v-if="!isReadOnlyPlan">
            <Button
              :label="t('PRICING.PLAN_DIALOG.UPDATE_BUTTON')"
              :is-loading="saving"
              :disabled="!planForm.name.trim() || saving"
              @click="handleSavePlan"
            />
          </span>
        </div>
      </div>

      <!-- Bundles -->
      <div
        v-if="bundles.length"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-5 mb-6 flex flex-col gap-3"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('PRICING.BUNDLES.TITLE') }}
        </h3>
        <div
          v-for="bundle in bundles"
          :key="bundle.pricing_item_id"
          class="rounded-lg border border-n-weak overflow-hidden"
        >
          <div
            class="flex items-center gap-2 px-4 py-2.5 bg-n-violet-3 border-b border-n-violet-6"
          >
            <Icon icon="i-lucide-package" class="size-4 text-n-violet-11" />
            <span class="text-sm font-medium text-n-violet-11">{{
              bundle.rootName
            }}</span>
          </div>
          <table v-if="bundle.includedItems.length" class="w-full text-sm">
            <thead>
              <tr
                class="border-b border-n-weak text-xs text-n-slate-10 uppercase"
              >
                <th class="px-4 py-2 text-left font-medium">
                  {{ t('PRICING.BUNDLES.ITEM') }}
                </th>
                <th class="px-4 py-2 text-left font-medium">
                  {{ t('PRICING.BUNDLES.CATEGORY') }}
                </th>
                <th class="px-4 py-2 text-left font-medium">
                  {{ t('PRICING.BUNDLES.UNIT') }}
                </th>
                <th class="px-4 py-2 text-right font-medium">
                  {{ t('PRICING.BUNDLES.QUANTITY') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="inc in bundle.includedItems"
                :key="inc.pricing_item_id"
                class="text-n-slate-11"
              >
                <td class="px-4 py-2.5 font-medium text-n-slate-12">
                  {{ inc.name }}
                </td>
                <td class="px-4 py-2.5">{{ inc.category || '—' }}</td>
                <td class="px-4 py-2.5">{{ inc.unit || '—' }}</td>
                <td class="px-4 py-2.5 text-right font-mono">
                  {{ inc.quantity }}
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="px-4 py-3 text-sm text-n-slate-10">
            {{ t('PRICING.BUNDLES.EMPTY') }}
          </p>
        </div>
      </div>
    </template>

    <template #header>
      <BreadcrumbTitle
        :items="breadcrumbItems"
        :description="t('PRICING.PLANS.DESCRIPTION')"
        @click="handleBreadcrumbClick"
      >
        <div class="flex items-center gap-2">
          <input
            v-model="searchQuery"
            type="text"
            class="reset-base rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-10 w-52"
            :placeholder="t('PRICING.MASTER_SHEET.SEARCH_PLACEHOLDER')"
          />
          <Button
            v-if="!isReadOnlyPlan"
            icon="i-lucide-plus"
            md
            :label="t('PRICING.RULES.ADD_RULE')"
            @click="openCreateRule"
          />
        </div>
      </BreadcrumbTitle>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak text-sm">
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.TABLE.ITEM_NAME') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.RULE_DIALOG.CLAUSES_LABEL') }}
            </th>
            <th
              class="py-4 ltr:text-right rtl:text-left font-semibold text-n-slate-11 w-24"
            >
              {{ t('PRICING.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="rule in filteredRules"
            :key="rule.id"
            class="border-b border-n-weak cursor-pointer"
            @click="openRule(rule)"
          >
            <td class="py-4">
              <div class="flex items-center gap-2">
                <Icon :icon="rule.itemIcon" class="size-5 text-n-slate-10" />
                <span class="font-medium text-neutral-900">{{
                  rule.itemName
                }}</span>
              </div>
            </td>
            <td class="py-4 text-n-slate-10">
              <template v-if="clauseCount(rule)">
                {{ clauseCount(rule) }}
                {{
                  clauseCount(rule) === 1
                    ? t('PRICING.RULES.CLAUSE_COUNT_SINGULAR')
                    : t('PRICING.RULES.CLAUSE_COUNT_PLURAL')
                }}
              </template>
              <template v-else>—</template>
            </td>
            <td class="py-4" @click.stop>
              <div
                v-if="!isReadOnlyPlan"
                class="flex items-center justify-end gap-2"
              >
                <Button
                  v-tooltip.top="t('PRICING.EDIT')"
                  icon="i-lucide-pen-line"
                  md
                  slate
                  faded
                  @click="openEditRule(rule)"
                />
                <Button
                  v-tooltip.top="t('PRICING.DELETE')"
                  icon="i-lucide-trash-2"
                  md
                  ruby
                  faded
                  @click="handleDeleteRule(rule)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>
</template>
